import { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, CheckCircle, Pencil, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import type { UserPickup } from "@/types/database";
import { EditPickupDialog } from "./EditPickupDialog";

interface ScheduledPickupBannerProps {
  userId: string;
  onPickupUpdated?: () => void;
}

export const ScheduledPickupBanner = ({ userId, onPickupUpdated }: ScheduledPickupBannerProps) => {
  const [activePickup, setActivePickup] = useState<UserPickup | null>(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    fetchActivePickup();
  }, [userId]);

  const fetchActivePickup = async () => {
    try {
      // Fetch both confirmed and awaiting_admin_confirmation pickups
      const { data, error } = await supabase
        .from("user_pickups")
        .select("*")
        .eq("user_id", userId)
        .in("status", ["confirmed", "awaiting_admin_confirmation"])
        .order("proposed_date", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setActivePickup(data as UserPickup | null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching active pickup:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    fetchActivePickup();
    onPickupUpdated?.();
  };

  if (loading || !activePickup) {
    return null;
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isPendingReApproval = activePickup.status === "awaiting_admin_confirmation";
  
  // For pending re-approval, show the newly requested date/time from counter_proposed fields
  const displayDate = isPendingReApproval && activePickup.counter_proposed_date 
    ? activePickup.counter_proposed_date 
    : activePickup.proposed_date;
  const displayTimeSlot = isPendingReApproval && activePickup.counter_proposed_time_slot 
    ? activePickup.counter_proposed_time_slot 
    : activePickup.proposed_time_slot;

  // Pending re-approval state (user requested a change)
  if (isPendingReApproval) {
    return (
      <Card data-pickup-banner className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 shadow-soft">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-full">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-amber-800 dark:text-amber-200 text-lg">
                      Pickup Change Requested
                    </h3>
                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700">
                      Pending Re-Approval
                    </Badge>
                  </div>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Awaiting logistics team approval
                  </p>
                </div>
              </div>
              
              <div className="p-3 bg-amber-100/50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-2">
                  Your requested new time:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">
                      {formatDate(displayDate)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">
                      {displayTimeSlot}
                    </span>
                  </div>
                  
                  <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium line-clamp-2">
                      {activePickup.address}
                    </span>
                  </div>
                </div>
              </div>

              {activePickup.user_message && (
                <p className="text-xs text-amber-600 dark:text-amber-400 italic">
                  Reason: "{activePickup.user_message}"
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Confirmed state (normal view)
  return (
    <>
      <Card data-pickup-banner className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 shadow-soft">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-full">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-green-800 dark:text-green-200 text-lg">
                    Scheduled Pickup
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Your packages will be picked up at this time
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditDialogOpen(true)}
                  className="flex-shrink-0 border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-900/50"
                >
                  <Pencil className="w-4 h-4 mr-1.5" />
                  Edit Time
                </Button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {formatDate(activePickup.proposed_date)}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {activePickup.proposed_time_slot}
                  </span>
                </div>
                
                <div className="flex items-start gap-2 text-green-700 dark:text-green-300">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium line-clamp-2">
                    {activePickup.address}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <EditPickupDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        pickup={activePickup}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};
